// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — own data: the DOM helper the My data tab and its panels build
 * with. **Text only:** strings become text nodes, never HTML, because
 * titles, file names, variable names, email addresses and the processor's
 * messages come from files and other people (security review L9).
 */

export type Child = Node | string | null | undefined | false
export type Props = Record<string, string | number | boolean | undefined>

/** An element with attributes and children; strings become text nodes, never HTML. */
export function h<K extends keyof HTMLElementTagNameMap>(tag: K, props: Props | null = null, ...children: Child[]): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag)
  for (const [key, value] of Object.entries(props ?? {})) {
    if (value === undefined || value === false) continue
    if (key === 'class') node.className = String(value)
    else if (key === 'disabled' || key === 'checked' || key === 'hidden' || key === 'required' || key === 'selected' || key === 'readOnly') (node as unknown as Record<string, boolean>)[key] = value === true
    else if (key === 'value') (node as unknown as { value: string }).value = String(value)
    else node.setAttribute(key, value === true ? '' : String(value))
  }
  for (const c of children) {
    if (c === null || c === undefined || c === false) continue
    node.append(typeof c === 'string' ? document.createTextNode(c) : c)
  }
  return node
}

export const button = (cls: string, label: string, props: Props = {}): HTMLButtonElement => h('button', { type: 'button', class: cls, ...props }, label)

/**
 * Redraw `root` with `draw`, keeping what the person was typing: the
 * values of text fields and selects (by id) and the focus survive the
 * redraw, except the ids in `reset` (a field cleared on purpose after it
 * was sent). A list that finishes loading must never wipe a half-typed
 * address or name.
 */
export function keepingDrafts(root: HTMLElement, draw: () => void, reset: readonly string[] = []): void {
  const drafts = new Map<string, string>()
  root.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>('input[id], select[id], textarea[id]').forEach((el) => {
    const type = el instanceof HTMLInputElement ? el.type : ''
    if (type === 'checkbox' || type === 'radio' || type === 'file' || reset.includes(el.id)) return
    if (el instanceof HTMLInputElement && el.readOnly) return
    drafts.set(el.id, el.value)
  })
  const focused = document.activeElement instanceof HTMLElement && root.contains(document.activeElement) ? document.activeElement.id : ''
  draw()
  for (const [id, value] of drafts) {
    const el = root.querySelector<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(`#${CSS.escape(id)}`)
    if (!el || (el instanceof HTMLInputElement && el.readOnly) || el.value === value) continue
    el.value = value
    // Whatever depends on the field (a button enabled by a typed title) follows it.
    el.dispatchEvent(new Event('input', { bubbles: true }))
  }
  if (focused && !reset.includes(focused)) root.querySelector<HTMLElement>(`#${CSS.escape(focused)}`)?.focus()
}
